import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';
import { isNativeApp } from '../platform/native';
import { bootMark } from '../boot/bootTrace';

/**
 * App Check — atestação de origem (anti-abuso). Blueprint e rollout em
 * `docs/systems/app-check.md`.
 *
 * Nascido do incidente de 26-27/08: cadastro no Firebase Auth NÃO passa pelo
 * gate de beta, então qualquer um com token válido bate nas callables e no
 * Firestore por URL. App Check exige prova de que a requisição vem do app
 * genuíno (reCAPTCHA no web; App Attest/Play Integrity no nativo), cortando
 * scanner automatizado.
 *
 * ⚠️ INVARIANTE 1: esta init NUNCA lança pra fora nem bloqueia o boot (BUG-114).
 * ⚠️ INVARIANTE 2: sem chave, é NO-OP — o app roda igual a antes de existir.
 */

/**
 * A decisão, pura e testável (padrão `resolvePauseTransition`/`resolveSettleClaim`).
 * Separada dos efeitos para o teste não precisar mockar o SDK do Firebase.
 */
export interface AppCheckEnv {
  useEmulator: boolean;
  isNative: boolean;
  hasWindow: boolean;
  isDev: boolean;
  siteKey: string | undefined;
  provider: string | undefined;
  debugToken: string | undefined;
}

export type AppCheckPlan =
  | { action: 'skip'; reason: string }
  | {
      action: 'init';
      provider: 'v3' | 'enterprise';
      siteKey: string;
      /** Só em DEV; null caso contrário (evita footgun de debug token em prod). */
      debugToken: string | null;
    };

export function resolveAppCheckPlan(env: AppCheckEnv): AppCheckPlan {
  // Emulador não impõe App Check, e o reCAPTCHA não existe ali.
  if (env.useEmulator) return { action: 'skip', reason: 'emulator' };
  // SSR / node de teste: sem window, sem reCAPTCHA.
  if (!env.hasWindow) return { action: 'skip', reason: 'no-window' };
  // Nativo: reCAPTCHA não roda no WebView (origem capacitor://). A atestação
  // nativa (App Attest/Play Integrity) é pendente do build nativo — ver doc.
  if (env.isNative) return { action: 'skip', reason: 'native-pending-appattest' };
  // Sem chave, App Check está desligado — estado atual do projeto.
  if (!env.siteKey) return { action: 'skip', reason: 'no-site-key' };

  const provider = env.provider === 'enterprise' ? 'enterprise' : 'v3';
  // Debug token só vale em DEV: um token de debug num build de produção
  // desligaria a atestação de verdade para quem tivesse o token.
  const debugToken = env.isDev && env.debugToken ? env.debugToken : null;
  return { action: 'init', provider, siteKey: env.siteKey, debugToken };
}

let appCheckInstance: AppCheck | null = null;

/** A instância viva, ou null se App Check está desligado nesta plataforma. */
export function getAppCheckInstance(): AppCheck | null {
  return appCheckInstance;
}

/**
 * Inicializa o App Check. Chamada uma vez, logo após `initializeApp`.
 * Fire-and-forget: `initializeAppCheck` retorna na hora e busca o token em
 * background — não há `await`, então não entra no caminho crítico do boot.
 */
export function initAppCheck(app: FirebaseApp): void {
  try {
    const env: AppCheckEnv = {
      useEmulator: import.meta.env.VITE_USE_EMULATOR === 'true',
      isNative: isNativeApp(),
      hasWindow: typeof window !== 'undefined',
      isDev: import.meta.env.DEV === true,
      siteKey: import.meta.env.VITE_APPCHECK_RECAPTCHA_KEY as string | undefined,
      provider: import.meta.env.VITE_APPCHECK_PROVIDER as string | undefined,
      debugToken: import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined,
    };

    const plan = resolveAppCheckPlan(env);
    if (plan.action === 'skip') {
      bootMark('appcheck', 'skip', plan.reason);
      return;
    }

    if (plan.debugToken) {
      // O SDK imprime o token no console para registrar em App Check → debug
      // tokens. `true` faz o SDK gerar um; string usa o token fixo.
      (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean })
        .FIREBASE_APPCHECK_DEBUG_TOKEN = plan.debugToken === 'true' ? true : plan.debugToken;
    }

    const provider =
      plan.provider === 'enterprise'
        ? new ReCaptchaEnterpriseProvider(plan.siteKey)
        : new ReCaptchaV3Provider(plan.siteKey);

    appCheckInstance = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });
    bootMark('appcheck', 'ok', `recaptcha-${plan.provider}`);
  } catch (error) {
    // App Check NUNCA derruba o boot. Falha aqui = app sem token, que em
    // monitor é inofensivo e em enforce é decisão de rollout (não deve chegar
    // aqui se a fase 2 respeitou os pré-requisitos).
    bootMark('appcheck', 'fail', String((error as Error)?.message ?? error));
    console.warn('[Lura] App Check init falhou (seguindo sem token).', error);
  }
}
